import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { verificationAPI } from '@/lib/api';
import { Camera, AlertCircle, Check, CreditCard, Clock, RefreshCw, Video, CircleDot, RotateCcw, ChevronRight, ChevronLeft } from 'lucide-react';

const FaceVerify = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [stream, setStream] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front, 'environment' for back camera
  
  // Captured images state
  const [capturedPhoto, setCapturedPhoto] = useState(null); // Live selfie
  const [idType, setIdType] = useState('');
  const [idFrontCapture, setIdFrontCapture] = useState(null); // Front of ID / Passport biodata
  const [idBackCapture, setIdBackCapture] = useState(null); // Back of ID (for license/national ID)
  
  // Current capture mode
  const [captureMode, setCaptureMode] = useState('selfie'); // 'selfie', 'id_front', 'id_back'

  const idTypes = [
    { value: 'passport', label: 'Passport (Biodata Page)', requiresBack: false },
    { value: 'drivers_license', label: "Driver's License (Front & Back)", requiresBack: true },
    { value: 'national_id', label: 'National ID Card (Front & Back)', requiresBack: true },
  ];

  const getSelectedIdType = () => idTypes.find(t => t.value === idType);
  const requiresBackCapture = () => getSelectedIdType()?.requiresBack ?? false;

  // Get capture instructions based on mode
  const getCaptureInstructions = () => {
    switch (captureMode) {
      case 'selfie':
        return {
          title: 'Live Selfie',
          description: 'Position your face in the oval frame. Ensure good lighting.',
          icon: <CircleDot className="w-4 h-4 mr-2 text-red-500" />
        };
      case 'id_front':
        if (idType === 'passport') {
          return {
            title: 'Passport Biodata Page',
            description: 'Open your passport to the photo/biodata page and capture it clearly.',
            icon: <CreditCard className="w-4 h-4 mr-2 text-blue-500" />
          };
        }
        return {
          title: `${idType === 'drivers_license' ? "Driver's License" : 'National ID'} - FRONT`,
          description: 'Capture the FRONT side of your ID with photo clearly visible.',
          icon: <CreditCard className="w-4 h-4 mr-2 text-blue-500" />
        };
      case 'id_back':
        return {
          title: `${idType === 'drivers_license' ? "Driver's License" : 'National ID'} - BACK`,
          description: 'Now flip your ID and capture the BACK side.',
          icon: <RotateCcw className="w-4 h-4 mr-2 text-purple-500" />
        };
      default:
        return { title: '', description: '', icon: null };
    }
  };

  // Start camera with specific facing mode
  const startCamera = useCallback(async (mode = 'user') => {
    try {
      // Stop existing stream first
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      setStream(mediaStream);
      setFacingMode(mode);
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(
        err.name === 'NotAllowedError' 
          ? 'Camera access denied. Please allow camera access in your browser settings.'
          : err.name === 'NotFoundError'
          ? 'No camera found. Please ensure your device has a camera.'
          : 'Failed to access camera. Please try again.'
      );
    }
  }, [stream]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  }, [stream]);

  // Switch camera (front/back)
  const switchCamera = async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    await startCamera(newMode);
  };

  // Capture photo with countdown
  const captureWithCountdown = () => {
    setCountdown(3);
  };

  // Handle countdown
  useEffect(() => {
    if (countdown === null) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      performCapture();
      setCountdown(null);
    }
  }, [countdown]);

  // Perform the actual capture
  const performCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Mirror only for selfie (front camera)
    if (captureMode === 'selfie' && facingMode === 'user') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    context.setTransform(1, 0, 0, 1, 0, 0);

    // Add timestamp watermark
    const timestamp = new Date().toISOString();
    context.fillStyle = 'rgba(255, 255, 255, 0.8)';
    context.fillRect(10, canvas.height - 30, 220, 22);
    context.fillStyle = '#000';
    context.font = '12px Arial';
    context.fillText(`Captured: ${timestamp}`, 15, canvas.height - 13);

    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    // Save to appropriate state based on capture mode
    switch (captureMode) {
      case 'selfie':
        setCapturedPhoto(imageData);
        toast.success('Selfie captured successfully!');
        break;
      case 'id_front':
        setIdFrontCapture(imageData);
        toast.success(idType === 'passport' ? 'Passport biodata captured!' : 'ID front captured!');
        break;
      case 'id_back':
        setIdBackCapture(imageData);
        toast.success('ID back captured!');
        break;
    }
    
    stopCamera();
  };

  // Retake current capture
  const retakeCapture = (mode) => {
    switch (mode) {
      case 'selfie':
        setCapturedPhoto(null);
        break;
      case 'id_front':
        setIdFrontCapture(null);
        break;
      case 'id_back':
        setIdBackCapture(null);
        break;
    }
    setCaptureMode(mode);
    // Use back camera for ID captures, front for selfie
    startCamera(mode === 'selfie' ? 'user' : 'environment');
  };

  // Start capture for a specific mode
  const startCapture = (mode) => {
    setCaptureMode(mode);
    startCamera(mode === 'selfie' ? 'user' : 'environment');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Check if all captures are complete
  const isComplete = () => {
    if (!capturedPhoto || !idType || !idFrontCapture) return false;
    if (requiresBackCapture() && !idBackCapture) return false;
    return true;
  };

  // Get current step number
  const getCurrentStep = () => {
    if (!capturedPhoto) return 1;
    if (!idType) return 2;
    if (!idFrontCapture) return 3;
    if (requiresBackCapture() && !idBackCapture) return 4;
    return 5;
  };

  const handleVerify = async () => {
    if (!isComplete()) {
      toast.error('Please complete all required captures');
      return;
    }

    setLoading(true);
    try {
      const livePhotoBase64 = capturedPhoto.split(',')[1];
      
      // Combine front and back into single document data if applicable
      let idDocBase64 = idFrontCapture.split(',')[1];
      let idBackBase64 = idBackCapture ? idBackCapture.split(',')[1] : null;

      const response = await verificationAPI.verifyFace(
        user.id, 
        livePhotoBase64, 
        idDocBase64, 
        idType,
        idBackBase64 // Pass back capture separately
      );
      
      toast.success(response.data.message || 'Verification submitted!');
      if (response.data.note) {
        toast.info(response.data.note);
      }
      
      setTimeout(() => {
        navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Only for individual users
  if (user.business_type !== 'individual') {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-chivo font-bold text-slate-900 mb-2">
            Not Required
          </h1>
          <p className="text-slate-600 mb-6">
            Face verification is only required for individual accounts. Business users should use document verification.
          </p>
          <Button onClick={() => navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const instructions = getCaptureInstructions();

  return (
    <div className="min-h-screen bg-surface py-6 px-4">
      <Card className="w-full max-w-2xl mx-auto p-6 md:p-8">
        <div className="text-center mb-6">
          <Camera className="w-14 h-14 text-accent mx-auto mb-3" />
          <h1 data-testid="face-verify-heading" className="text-2xl md:text-3xl font-chivo font-bold text-slate-900 mb-2">
            Identity Verification
          </h1>
          <p className="text-slate-600 text-sm">
            Complete all steps using your device camera. No file uploads allowed.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((step) => {
            const totalSteps = requiresBackCapture() ? 4 : 3;
            if (step > totalSteps && step === 4) return null;
            return (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  getCurrentStep() > step 
                    ? 'bg-green-500 text-white' 
                    : getCurrentStep() === step 
                    ? 'bg-accent text-white' 
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {getCurrentStep() > step ? <Check className="w-4 h-4" /> : step}
                </div>
                {step < totalSteps && (
                  <ChevronRight className="w-4 h-4 text-slate-300 mx-1" />
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-6">
          
          {/* STEP 1: Live Selfie */}
          <div className={`p-4 rounded-xl border-2 ${capturedPhoto ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold flex items-center">
                <CircleDot className="w-4 h-4 mr-2 text-red-500" />
                Step 1: Live Selfie
              </Label>
              {capturedPhoto && (
                <span className="text-green-600 text-sm flex items-center">
                  <Check className="w-4 h-4 mr-1" /> Captured
                </span>
              )}
            </div>
            
            {capturedPhoto ? (
              <div className="flex items-center gap-4">
                <img src={capturedPhoto} alt="Selfie" className="w-24 h-24 object-cover rounded-lg" />
                <Button variant="outline" size="sm" onClick={() => retakeCapture('selfie')}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Retake
                </Button>
              </div>
            ) : (
              <Button onClick={() => startCapture('selfie')} className="w-full">
                <Camera className="w-4 h-4 mr-2" /> Start Selfie Capture
              </Button>
            )}
          </div>

          {/* STEP 2: ID Type Selection */}
          <div className={`p-4 rounded-xl border-2 ${idType ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold flex items-center">
                <CreditCard className="w-4 h-4 mr-2 text-blue-500" />
                Step 2: Select ID Type
              </Label>
              {idType && (
                <span className="text-green-600 text-sm flex items-center">
                  <Check className="w-4 h-4 mr-1" /> Selected
                </span>
              )}
            </div>
            
            <Select 
              value={idType} 
              onValueChange={(v) => {
                setIdType(v);
                // Reset ID captures when type changes
                setIdFrontCapture(null);
                setIdBackCapture(null);
              }}
              disabled={!capturedPhoto}
            >
              <SelectTrigger data-testid="id-type-select" className={!capturedPhoto ? 'opacity-50' : ''}>
                <SelectValue placeholder={capturedPhoto ? "Select your ID type" : "Complete Step 1 first"} />
              </SelectTrigger>
              <SelectContent>
                {idTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {idType && (
              <p className="text-xs text-slate-500 mt-2">
                {requiresBackCapture() 
                  ? 'You will need to capture BOTH sides of your ID'
                  : 'You only need to capture the biodata page'
                }
              </p>
            )}
          </div>

          {/* STEP 3: ID Front / Passport Biodata */}
          {idType && (
            <div className={`p-4 rounded-xl border-2 ${idFrontCapture ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold flex items-center">
                  <CreditCard className="w-4 h-4 mr-2 text-blue-500" />
                  Step 3: {idType === 'passport' ? 'Passport Biodata' : 'ID Front Side'}
                </Label>
                {idFrontCapture && (
                  <span className="text-green-600 text-sm flex items-center">
                    <Check className="w-4 h-4 mr-1" /> Captured
                  </span>
                )}
              </div>
              
              {idFrontCapture ? (
                <div className="flex items-center gap-4">
                  <img src={idFrontCapture} alt="ID Front" className="w-32 h-20 object-cover rounded-lg" />
                  <Button variant="outline" size="sm" onClick={() => retakeCapture('id_front')}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Retake
                  </Button>
                </div>
              ) : (
                <Button onClick={() => startCapture('id_front')} className="w-full">
                  <Camera className="w-4 h-4 mr-2" /> 
                  Capture {idType === 'passport' ? 'Biodata Page' : 'Front Side'}
                </Button>
              )}
            </div>
          )}

          {/* STEP 4: ID Back (for license/national ID only) */}
          {idType && requiresBackCapture() && idFrontCapture && (
            <div className={`p-4 rounded-xl border-2 ${idBackCapture ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold flex items-center">
                  <RotateCcw className="w-4 h-4 mr-2 text-purple-500" />
                  Step 4: ID Back Side
                </Label>
                {idBackCapture && (
                  <span className="text-green-600 text-sm flex items-center">
                    <Check className="w-4 h-4 mr-1" /> Captured
                  </span>
                )}
              </div>
              
              {idBackCapture ? (
                <div className="flex items-center gap-4">
                  <img src={idBackCapture} alt="ID Back" className="w-32 h-20 object-cover rounded-lg" />
                  <Button variant="outline" size="sm" onClick={() => retakeCapture('id_back')}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Retake
                  </Button>
                </div>
              ) : (
                <Button onClick={() => startCapture('id_back')} className="w-full bg-purple-600 hover:bg-purple-700">
                  <Camera className="w-4 h-4 mr-2" /> Capture Back Side
                </Button>
              )}
            </div>
          )}

          {/* Camera Capture Modal */}
          {cameraActive && (
            <div className="fixed inset-0 bg-black z-50 flex flex-col">
              {/* Header */}
              <div className="bg-black/80 text-white p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold flex items-center">
                    {instructions.icon}
                    {instructions.title}
                  </h3>
                  <p className="text-sm text-slate-300">{instructions.description}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={stopCamera} className="text-white hover:bg-white/20">
                  Cancel
                </Button>
              </div>

              {/* Camera View */}
              <div className="flex-1 relative">
                <canvas ref={canvasRef} className="hidden" />
                
                {cameraError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                    <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
                    <p className="text-red-400 text-center text-sm mb-4">{cameraError}</p>
                    <Button onClick={() => startCamera(facingMode)} variant="outline" size="sm">
                      Try Again
                    </Button>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover ${captureMode === 'selfie' && facingMode === 'user' ? 'transform scale-x-[-1]' : ''}`}
                    />
                    
                    {/* Guide overlay for selfie */}
                    {captureMode === 'selfie' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-52 h-72 border-4 border-white/60 rounded-[50%]"></div>
                      </div>
                    )}

                    {/* Guide overlay for ID capture */}
                    {captureMode !== 'selfie' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-[85%] max-w-md aspect-[1.6/1] border-4 border-white/60 rounded-lg">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <p className="text-white/70 text-sm bg-black/40 px-3 py-1 rounded">
                              Align {captureMode === 'id_back' ? 'back of' : ''} ID within frame
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Countdown overlay */}
                    {countdown !== null && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <span className="text-8xl font-bold text-white animate-pulse">
                          {countdown}
                        </span>
                      </div>
                    )}

                    {/* Live indicator */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      LIVE
                    </div>
                  </>
                )}
              </div>

              {/* Controls */}
              <div className="bg-black/80 p-6 flex items-center justify-center gap-4">
                {captureMode !== 'selfie' && (
                  <Button
                    onClick={switchCamera}
                    variant="outline"
                    size="icon"
                    className="rounded-full w-12 h-12 bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                )}
                
                <Button 
                  onClick={captureWithCountdown}
                  className="w-20 h-20 rounded-full bg-white hover:bg-slate-100"
                  disabled={countdown !== null}
                >
                  <div className="w-16 h-16 rounded-full border-4 border-slate-800" />
                </Button>
                
                <div className="w-12" /> {/* Spacer for alignment */}
              </div>
            </div>
          )}

          {/* Info Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Camera Capture Only
              </h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• All photos must be taken live</li>
                <li>• File uploads are not accepted</li>
                <li>• Ensure good lighting</li>
                <li>• Hold device steady</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Review Process
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Reviewed by our team</li>
                <li>• Takes 24-48 hours</li>
                <li>• Email notification sent</li>
              </ul>
            </div>
          </div>

          <Button
            data-testid="submit-face-verify-btn"
            className="w-full h-12 text-base"
            onClick={handleVerify}
            disabled={loading || !isComplete()}
          >
            {loading ? 'Submitting...' : 'Submit for Verification'}
          </Button>
        </div>

        <div className="mt-4 text-center">
          <Button
            data-testid="skip-face-verify-btn"
            variant="ghost"
            size="sm"
            onClick={() => navigate(user.role === 'buyer' ? '/buyer/dashboard' : '/seller/dashboard')}
          >
            Skip for now
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default FaceVerify;
